import React from "react";
import { NavLink } from "react-router-dom";
import "./styles.scss";
import { SidebarItem, iconsMap } from "./types";
import { useAuth } from "features/auth/hooks/useAuth";
import { architectureRoutes } from "features/architecture/routes/routes";
import { systemAdminRoutes } from "features/system-admin/routes";

const ARCHITECTURE_BASE_PATH = "/home/architecture";

const stripRouteParams = (path: string): string =>
  path.replace(/\/:[^/]+\??/g, "");

const buildArchitectureLink = (path: string): string =>
  `${ARCHITECTURE_BASE_PATH}/${stripRouteParams(path)}`;

const toSidebarItem = (
  route: (typeof architectureRoutes)[number],
): SidebarItem | null => {
  const sidebar = route.meta?.sidebar;

  if (!sidebar || sidebar.hidden) {
    return null;
  }

  return {
    name: sidebar.label,
    iconName: sidebar.iconName as SidebarItem["iconName"],
    domain: route.domain,
    requiredPermissions: route.requiredPermissions,
    requiredPermissionsAny: route.requiredPermissionsAny,
    requiredPermissionScopesAny: route.requiredPermissionScopesAny,
    to: buildArchitectureLink(route.path),
  };
};

const toAdminSidebarItem = (
  route: (typeof systemAdminRoutes)[number],
): SidebarItem | null => {
  const sidebar = route.meta?.sidebar;

  if (!sidebar || sidebar.hidden || route.path.includes(":")) {
    return null;
  }

  return {
    name: sidebar.label,
    iconName: sidebar.iconName as SidebarItem["iconName"],
    domain: route.domain,
    requiredPermissions: route.requiredPermissions,
    requiredPermissionsAny: route.requiredPermissionsAny,
    requiredPermissionScopesAny: route.requiredPermissionScopesAny,
    to: route.path,
  };
};

const hasRequiredScope = (
  getPermissionScope: ReturnType<typeof useAuth>["getPermissionScope"],
  item: SidebarItem,
) => {
  if (!item.requiredPermissionScopesAny?.length) return true;

  return item.requiredPermissionScopesAny.some(({ permission, scopes }) => {
    const scope = getPermissionScope(permission);
    return scope != null && scopes.includes(scope);
  });
};

export const sidebarItems: SidebarItem[] = [
  {
    name: "Home",
    iconName: "Home",
    to: "/home",
  },
  ...architectureRoutes
    .map(toSidebarItem)
    .filter((item): item is SidebarItem => item !== null),
  ...systemAdminRoutes
    .map(toAdminSidebarItem)
    .filter((item): item is SidebarItem => item !== null),
];

export const Sidebar: React.FC = () => {
  const {
    isLoading: loading,
    hasDomainAccess,
    hasPermission,
    getPermissionScope,
  } = useAuth();

  if (loading) return <aside className="sidebar">Loading menu...</aside>;

  return (
    <aside className="sidebar">
      <div className="sidebar-nav">
        {sidebarItems
          .filter((item) => {
            const isDomainAllowed =
              !item.domain || hasDomainAccess(item.domain);
            const isVisibleByPermission =
              !item.requiredPermissions?.length ||
              item.requiredPermissions.every((permission) =>
                hasPermission(permission),
              );
            const isVisibleByPermissionAny =
              !item.requiredPermissionsAny?.length ||
              item.requiredPermissionsAny.some((permission) =>
                hasPermission(permission),
              );
            const isVisibleByScope = hasRequiredScope(getPermissionScope, item);

            return (
              isDomainAllowed &&
              isVisibleByPermission &&
              isVisibleByPermissionAny &&
              isVisibleByScope
            );
          })
          .map((item, index) => {
            const Icon = iconsMap[item.iconName];
            return (
              <NavLink
                key={index}
                to={item.to ?? ""}
                end
                title={item.name}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
              >
                {({ isActive }) => (
                  <Icon className={`nav-icon ${isActive ? "active" : ""}`} />
                )}
              </NavLink>
            );
          })}
      </div>
    </aside>
  );
};
